import * as React from "react"
import Box from "@mui/material/Box"
import TextField from "@mui/material/TextField"
import Autocomplete from "@mui/material/Autocomplete"
import PersonIcon from "@mui/icons-material/Person"
import DeletedPersonIcon from "@mui/icons-material/PersonOff"
import Grid from "@mui/material/Grid"
import Typography from "@mui/material/Typography"
import parse from "autosuggest-highlight/parse"
import match from "autosuggest-highlight/match"
import SearchIcon from "@mui/icons-material/Search"
import { styled } from "@mui/material/styles"
import { CircularProgress, Paper } from "@mui/material"
import { SxProps } from "@mui/system"

import { omit } from "lodash"
import { UserSearch } from "../../../types"
import { formatPhoneNumber } from "../../../utils/misc"
import { makeApiCall } from "../../../utils/gatewayUtils"
import apiConfig from "../../../apiConfig"
import { useAuth } from "../../AuthProvider"

type Props = {
  userSelectedCallback: (user: UserSearch) => void
  onClear?: () => void
  excludeIds?: string[]
  isFocused?: boolean
  autoFocus?: boolean
  styles?: SxProps
  size?: "small" | "medium"
  useKeyCommand?: boolean
  onBlurCallback?: () => void
}

const NO_OPTIONS_STATES = {
  empty: "Press enter to search",
  noResults: "No results found",
  searching: "Searching...",
  error: "There was an error, please try again later",
}

const StyledTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
  },
}))

/**
 * This is heavily based on the example in the Mui docs:
 * https://mui.com/material-ui/react-autocomplete/#asynchronous-requests
 */

function buildUserResultString(result: UserSearch) {
  let primary = `${result.name}, ${result.email}`
  if (result.phone) {
    primary += `, ${formatPhoneNumber(result.phone)}`
  }
  let secondary = ""
  if (result.altName) {
    secondary += `${result.altName}`
  }
  if (result.altEmail) {
    secondary += `, ${result.altEmail}`
  }
  if (result.altPhone) {
    secondary += `, ${formatPhoneNumber(result.altPhone)}`
  }
  return {
    primary,
    secondary,
  }
}

export default function StudentAutocomplete({
  userSelectedCallback,
  onClear,
  excludeIds,
  isFocused,
  autoFocus = true,
  styles,
  size = "medium",
  useKeyCommand = false,
  onBlurCallback,
}: Props) {
  const { getCurrentUserToken } = useAuth()
  const [value, setValue] = React.useState<UserSearch | null>(null)
  const [inputValue, setInputValue] = React.useState("")
  const [options, setOptions] = React.useState<readonly UserSearch[]>([])
  const [isSearching, setIsSearching] = React.useState(false)
  const [isError, setIsError] = React.useState(false)
  const [didDoSearch, setDidDoSearch] = React.useState(false)
  const [noOptionsText, setNoOptionsText] = React.useState(
    NO_OPTIONS_STATES.empty,
  )
  const abortRef = React.useRef(new window.AbortController())

  React.useEffect(() => {
    if (didDoSearch) {
      if (isSearching) {
        setNoOptionsText(NO_OPTIONS_STATES.searching)
      } else if (isError) {
        setNoOptionsText(NO_OPTIONS_STATES.error)
      } else {
        setNoOptionsText(NO_OPTIONS_STATES.noResults)
      }
    } else {
      setNoOptionsText(NO_OPTIONS_STATES.empty)
    }
  }, [isSearching, didDoSearch, isError])

  const handleSearch = async () => {
    if (inputValue === "") {
      return
    }

    setIsSearching(true)
    setDidDoSearch(true)

    if (abortRef.current) {
      abortRef.current.abort()
    }
    abortRef.current = new window.AbortController()

    const results = await getCurrentUserToken(false)
      .then((idToken) =>
        makeApiCall(
          `${apiConfig.userSearch}?query=${encodeURIComponent(inputValue)}`,
          {
            method: "GET",
            headers: { authorization: `Bearer ${idToken}` },
            signal: abortRef.current.signal,
          },
        ),
      )
      .catch(() => {
        setIsError(true)
      })

    let newOptions: readonly UserSearch[] = []

    if (results) {
      newOptions = [...newOptions, ...results]
    }

    const filteredOptions = newOptions.filter(
      (option) => !excludeIds || !excludeIds.includes(option.id),
    )

    setIsSearching(false)
    setOptions(filteredOptions)
  }

  return (
    <Autocomplete
      sx={styles}
      size={size}
      clearOnBlur={false}
      getOptionLabel={(option) => buildUserResultString(option).primary}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      onBlur={onBlurCallback}
      includeInputInList
      filterSelectedOptions
      value={value}
      noOptionsText={noOptionsText}
      onChange={(event: any, newValue: UserSearch | null) => {
        setValue(newValue)
        if (newValue) {
          userSelectedCallback(newValue)
        }

        if (!newValue && onClear) {
          onClear()
        }
      }}
      onInputChange={(event, newInputValue) => {
        setOptions([])
        setDidDoSearch(false)
        setInputValue(newInputValue)
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault()
          handleSearch()
        }
      }}
      renderInput={(params) => (
        <StyledTextField
          data-cy="student-autocomplete"
          {...params}
          label="Search name, email or phone number"
          autoFocus={autoFocus}
          inputRef={(input) => {
            if (isFocused && input) {
              input.focus()
            }
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: useKeyCommand ? <SearchIcon /> : null,
            endAdornment: (
              <div style={{ display: "flex" }}>
                {isSearching ? <CircularProgress size={20} /> : null}
                {useKeyCommand ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: "2px",
                      minWidth: "25px",
                      minHeight: "25px",
                      textAlign: "center",
                      backgroundColor: "#FAFAFA",
                    }}
                  >
                    {" "}
                    /
                  </Paper>
                ) : (
                  params.InputProps.endAdornment
                )}
              </div>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const optionString = buildUserResultString(option).primary
        const matches = match(optionString, inputValue)
        const parts = parse(optionString, matches)

        return (
          <li
            // spreading props with "key" prints a warning
            {...omit(props, "key")}
            key={props.key}
            data-cy="student-suggestion"
          >
            <Grid container alignItems="center">
              <Grid item>
                <Box
                  component={
                    option.isMigrated || option.isDeleted
                      ? DeletedPersonIcon
                      : PersonIcon
                  }
                  sx={{ color: "text.secondary", mr: 2 }}
                />
              </Grid>
              <Grid item xs>
                {parts.map((part, index) => (
                  <span
                    key={index}
                    style={{
                      fontWeight: part.highlight ? 700 : 400,
                    }}
                  >
                    {part.text}
                  </span>
                ))}
                <Typography variant="body2" color="text.secondary">
                  {buildUserResultString(option).secondary}
                </Typography>
              </Grid>
            </Grid>
          </li>
        )
      }}
    />
  )
}
